import { Enemy } from './Enemy';
import type { World } from '../World';
import { gameplayRng } from '../../core/rng/RngStreams';
import { SIM_HZ } from '../../core/GameLoop';
import { RIME_SLOW } from '../abilities/AbilitySystem';
import { GOBLIN_AGGRO_RADIUS, GOBLIN_WANDER_SPEED } from './enemyDefs';
import { TAU } from '../../core/math';

/**
 * Position-reactive movement — deliberately unseeded (design doc: the seed
 * fixes schedules and shapes, never moment-to-moment behavior).
 */

const ACTIVE_RANGE = 900; // beyond this, enemies idle
// Per-type runner gate ranges live on EnemyDef.fireRange (default 680).

const LEECH_PULSE_PERIOD = 3.5; // seconds between heal pulses
const LEECH_PULSE_RADIUS = 140;

export function updateEnemy(e: Enemy, world: World, dt: number): void {
  e.prevX = e.x;
  e.prevY = e.y;
  if (e.flash > 0) e.flash--;

  // Stun: no movement or behavior at all (timer decays in tickDebuffs).
  if (e.stunLeft > 0) return;

  // Chill/zone slows: applied as a scoped speed scale around the behavior
  // switch so every movement site (chase, strafe, dash approach) inherits
  // them uniformly; restored below.
  const trueSpeed = e.speed;
  if (e.chillLeft > 0) {
    e.chillLeft -= dt;
    e.speed *= RIME_SLOW;
    if (e.chillLeft <= 0) e.chillShatter = false;
  }
  if (e.zoneSlowLeft > 0) e.speed *= e.zoneSlowMult;

  try {
    updateEnemyInner(e, world, dt);
  } finally {
    e.speed = trueSpeed;
  }
}

function updateEnemyInner(e: Enemy, world: World, dt: number): void {
  const p = world.player;
  const dx = p.x - e.x;
  const dy = p.y - e.y;
  const dist = Math.hypot(dx, dy) || 1;
  if (dist > ACTIVE_RANGE) return;

  switch (e.type) {
    case 'shambler':
    case 'splitter': // menace is its death-burst; movement is plain chase
    case 'mite': {   // menace is speed
      const [cx, cy] = world.chaseDir(e.x, e.y);
      moveWithWalls(e, world, cx * e.speed * dt, cy * e.speed * dt);
      break;
    }

    case 'watcher': {
      // Hold ~260px range, strafing sideways; flip direction occasionally.
      if (gameplayRng.next() < 0.008) e.strafeDir *= -1;
      const radial = dist > 300 ? 1 : dist < 220 ? -1 : 0;
      const [cx, cy] = radial >= 0 ? world.chaseDir(e.x, e.y) : [dx / dist, dy / dist];
      const tx = cx * radial + -cy * 0.7 * e.strafeDir;
      const ty = cy * radial + cx * 0.7 * e.strafeDir;
      const tm = Math.hypot(tx, ty) || 1;
      moveWithWalls(e, world, (tx / tm) * e.speed * dt, (ty / tm) * e.speed * dt);
      break;
    }

    case 'husk':
      updateHusk(e, world, dt, dx, dy, dist);
      break;

    case 'chanter':
    case 'brute': {
      // Close to near-melee, then park and slam (runner fires via fireRange).
      const park = e.type === 'chanter' ? 160 : 180;
      if (dist > park) {
        const [cx, cy] = world.chaseDir(e.x, e.y);
        moveWithWalls(e, world, cx * e.speed * dt, cy * e.speed * dt);
      }
      break;
    }

    case 'flanker':
      updateFlanker(e, world, dt, dx, dy, dist);
      break;

    case 'leech': {
      const [cx, cy] = world.chaseDir(e.x, e.y);
      moveWithWalls(e, world, cx * e.speed * dt, cy * e.speed * dt);
      // Heal pulse on a FIXED timer — no rng, seeded or otherwise. Sentinel
      // 0 = unstarted: arm the timer on the first active tick, don't pulse.
      if (e.pulseLeft === 0) {
        e.pulseLeft = LEECH_PULSE_PERIOD;
        break;
      }
      e.pulseLeft -= dt;
      if (e.pulseLeft <= 0) {
        e.pulseLeft = LEECH_PULSE_PERIOD;
        let healed = false;
        world.hash.query(e.x, e.y, LEECH_PULSE_RADIUS, (idx) => {
          const t = world.enemies[idx];
          if (!t || !t.alive || t === e) return;
          if (t.type === 'boss' || t.type === 'keeper') return; // no boss sustain
          if (t.hp < t.maxHp) {
            t.hp = Math.min(t.maxHp, t.hp + Math.ceil(t.maxHp * 0.15));
            t.flash = 2;
            healed = true;
          }
        });
        world.events.emit('leechPulse', { x: e.x, y: e.y, radius: LEECH_PULSE_RADIUS, healed });
      }
      break;
    }

    case 'keeper': {
      // Vault guardian: slow, relentless drift toward the player. The vault
      // is tiny, so no leash and no flow field needed — but walls apply.
      const [cx, cy] = world.chaseDir(e.x, e.y);
      moveWithWalls(e, world, cx * e.speed * dt, cy * e.speed * dt);
      break;
    }

    case 'goblin': {
      // Prey: calm wander until spooked (proximity here, first damage in
      // damageEnemy), then flat-out flight up the flow field. The escape
      // clock ticks World-side (tickGoblin) so it survives ACTIVE_RANGE.
      if (e.panicLeft > 0) {
        if (gameplayRng.next() < 0.01) e.strafeDir *= -1; // vary the squirm
        const [fx, fy] = world.fleeDir(e);
        moveWithWalls(e, world, fx * e.speed * dt, fy * e.speed * dt);
      } else if (dist < GOBLIN_AGGRO_RADIUS) {
        world.panicGoblin(e);
      } else {
        // Idle wander: gameplayRng heading, rerolled every 1–2s.
        e.wanderLeft -= dt;
        if (e.wanderLeft <= 0) {
          e.wanderLeft = 1 + gameplayRng.next();
          e.wanderAng = gameplayRng.range(0, TAU);
        }
        moveWithWalls(
          e, world,
          Math.cos(e.wanderAng) * e.speed * GOBLIN_WANDER_SPEED * dt,
          Math.sin(e.wanderAng) * e.speed * GOBLIN_WANDER_SPEED * dt,
        );
      }
      break;
    }

    case 'boss': {
      // Hover at mid range, slow strafing drift; stay inside the arena.
      if (gameplayRng.next() < 0.006) e.strafeDir *= -1;
      const radial = dist > 320 ? 1 : dist < 200 ? -1 : 0;
      const tx = (dx / dist) * radial + (-dy / dist) * 0.5 * e.strafeDir;
      const ty = (dy / dist) * radial + (dx / dist) * 0.5 * e.strafeDir;
      const tm = Math.hypot(tx, ty) || 1;
      moveWithWalls(e, world, (tx / tm) * e.speed * dt, (ty / tm) * e.speed * dt);
      const arena = world.arena;
      if (arena) {
        if (e.x < arena.minX + e.radius) e.x = arena.minX + e.radius;
        if (e.x > arena.maxX - e.radius) e.x = arena.maxX - e.radius;
        if (e.y < arena.minY + e.radius) e.y = arena.minY + e.radius;
        if (e.y > arena.maxY - e.radius) e.y = arena.maxY - e.radius;
      }
      break;
    }
  }

  // Firing cycle: seeded shapes, reactively gated by the def's range.
  if (e.runner && dist < e.fireRange) {
    e.runner.tick(world.patternCtx, e.x, e.y);
  }
}

function updateHusk(e: Enemy, world: World, dt: number, dx: number, dy: number, dist: number): void {
  switch (e.state) {
    case 'approach':
      if (dist < 240) {
        e.state = 'windup';
        e.stateLeft = Math.round(0.45 * SIM_HZ);
        // Commit the dash to the current player direction and telegraph it.
        const d = dist || 1;
        e.dashVx = (dx / d) * 640;
        e.dashVy = (dy / d) * 640;
        world.telegraphs.spawnLine(e.x, e.y, Math.atan2(dy, dx), dist + 120, 34, e.stateLeft);
      } else {
        const [cx, cy] = world.chaseDir(e.x, e.y);
        moveWithWalls(e, world, cx * e.speed * dt, cy * e.speed * dt);
      }
      break;
    case 'windup':
      if (--e.stateLeft <= 0) {
        e.state = 'dash';
        e.stateLeft = Math.round(0.32 * SIM_HZ);
      }
      break;
    case 'dash':
      moveWithWalls(e, world, e.dashVx * dt, e.dashVy * dt);
      if (--e.stateLeft <= 0) {
        e.state = 'recover';
        e.stateLeft = Math.round(0.6 * SIM_HZ);
      }
      break;
    case 'recover':
      if (--e.stateLeft <= 0) e.state = 'approach';
      break;
  }
}

/**
 * Flanker: orbits the player at mid range, then commits a telegraphed
 * side-dash. Reuses the husk DashState fields; because the commit happens
 * FROM the orbit ring, the dash arrives laterally by pure geometry.
 */
function updateFlanker(e: Enemy, world: World, dt: number, dx: number, dy: number, dist: number): void {
  switch (e.state) {
    case 'approach': {
      if (dist > 340) {
        const [cx, cy] = world.chaseDir(e.x, e.y);
        moveWithWalls(e, world, cx * e.speed * dt, cy * e.speed * dt);
        break;
      }
      // Orbit the 240–320 band, tangential-dominant.
      if (gameplayRng.next() < 0.006) e.strafeDir *= -1;
      const radial = dist > 320 ? 1 : dist < 240 ? -1 : 0;
      const [cx, cy] = radial >= 0 ? world.chaseDir(e.x, e.y) : [dx / dist, dy / dist];
      const tx = cx * radial + -cy * 1.0 * e.strafeDir;
      const ty = cy * radial + cx * 1.0 * e.strafeDir;
      const tm = Math.hypot(tx, ty) || 1;
      moveWithWalls(e, world, (tx / tm) * e.speed * dt, (ty / tm) * e.speed * dt);
      // While holding the band, occasionally commit (~1.4s expected).
      if (radial === 0 && gameplayRng.next() < 0.012) {
        e.state = 'windup';
        e.stateLeft = Math.round(0.35 * SIM_HZ);
        const d = dist || 1;
        e.dashVx = (dx / d) * 700;
        e.dashVy = (dy / d) * 700;
        world.telegraphs.spawnLine(e.x, e.y, Math.atan2(dy, dx), dist + 100, 30, e.stateLeft);
      }
      break;
    }
    case 'windup':
      if (--e.stateLeft <= 0) {
        e.state = 'dash';
        e.stateLeft = Math.round(0.3 * SIM_HZ);
      }
      break;
    case 'dash':
      moveWithWalls(e, world, e.dashVx * dt, e.dashVy * dt);
      if (--e.stateLeft <= 0) {
        e.state = 'recover';
        e.stateLeft = Math.round(0.7 * SIM_HZ);
      }
      break;
    case 'recover':
      if (--e.stateLeft <= 0) e.state = 'approach';
      break;
  }
}

function moveWithWalls(e: Enemy, world: World, mx: number, my: number): void {
  [e.x, e.y] = world.tileMap.resolveMove(e.x, e.y, e.x + mx, e.y + my, e.radius * 0.8);
}
